import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Tab,
  Tabs,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Inventory,
  AccountBalance,
  Download,
  DateRange,
} from '@mui/icons-material';
import { reportsAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const Reports = () => {
  const [tabValue, setTabValue] = useState(0);
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);
  const [profitLossReport, setProfitLossReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
  });

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchReports();
  }, [tabValue, dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      switch (tabValue) {
        case 0: // Sales Report
          const salesResponse = await reportsAPI.getSalesReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            groupBy: 'day',
          });
          setSalesReport(salesResponse.data.data);
          break;
        
        case 1: // Inventory Report
          const inventoryResponse = await reportsAPI.getInventoryReport();
          setInventoryReport(inventoryResponse.data.data);
          break;
        
        case 2: // Profit & Loss
          const profitLossResponse = await reportsAPI.getProfitLossReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          });
          setProfitLossReport(profitLossResponse.data.data);
          break;
        
        default:
          break;
      }
    } catch (error) {
      enqueueSnackbar('Error fetching report data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExport = () => {
    enqueueSnackbar('Export feature coming soon!', { variant: 'info' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          Reports & Analytics
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
        >
          Export Report
        </Button>
      </Box>

      {/* Date Range Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <DateRange color="action" />
            </Grid>
            <Grid item>
              <TextField
                type="date"
                label="Start Date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <TextField
                type="date"
                label="End Date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={fetchReports}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<TrendingUp />} label="Sales Report" />
            <Tab icon={<Inventory />} label="Inventory Report" />
            <Tab icon={<AccountBalance />} label="Profit & Loss" />
          </Tabs>

          {loading && <LinearProgress />}

          {/* Sales Report Tab */}
          <TabPanel value={tabValue} index={0}>
            {salesReport && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Sales Summary ({new Date(salesReport.period.start).toLocaleDateString()} - {new Date(salesReport.period.end).toLocaleDateString()})
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {salesReport.summary.totalSales}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Sales
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          ₹{salesReport.summary.totalRevenue.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Revenue
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="info.main">
                          ₹{salesReport.summary.totalCost.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Cost
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          ₹{salesReport.summary.totalProfit.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Profit
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Top Selling Medicines */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Top Selling Medicines
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Medicine</TableCell>
                            <TableCell align="center">Quantity Sold</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">Cost</TableCell>
                            <TableCell align="right">Profit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {salesReport.topSellingMedicines.map((medicine, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {medicine.medicine.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {medicine.medicine.genericName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={medicine.quantity} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" color="success.main">
                                  ₹{medicine.revenue.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  ₹{medicine.cost.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" fontWeight="bold">
                                  ₹{(medicine.revenue - medicine.cost).toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Inventory Report Tab */}
          <TabPanel value={tabValue} index={1}>
            {inventoryReport && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Inventory Overview
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold">
                          {inventoryReport.summary.totalMedicines}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Medicines
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          {inventoryReport.summary.lowStockCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Low Stock Items
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="error.main">
                          {inventoryReport.summary.expiringCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Expiring Soon
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          ₹{inventoryReport.summary.totalValue.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Value
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Inventory by Category */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      Inventory by Category
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell align="center">Medicines</TableCell>
                            <TableCell align="center">Total Quantity</TableCell>
                            <TableCell align="right">Total Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inventoryReport.inventoryByCategory.map((category, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {category.category}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip label={category.medicines} size="small" />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="subtitle2">
                                  {category.totalQuantity}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="subtitle2" color="success.main">
                                  ₹{category.totalValue.toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Box>
            )}
          </TabPanel>

          {/* Profit & Loss Tab */}
          <TabPanel value={tabValue} index={2}>
            {profitLossReport && (
              <Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Profit & Loss Statement ({new Date(profitLossReport.period.start).toLocaleDateString()} - {new Date(profitLossReport.period.end).toLocaleDateString()})
                </Typography>
                
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Revenue
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Total Revenue:</Typography>
                          <Typography variant="h6" color="success.main">
                            ₹{profitLossReport.revenue.total.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Cost of Goods Sold:</Typography>
                          <Typography color="error.main">
                            ₹{profitLossReport.revenue.cogs.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Gross Profit:</Typography>
                          <Typography variant="h6" color="primary.main">
                            ₹{profitLossReport.revenue.grossProfit.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography>Gross Margin:</Typography>
                          <Typography variant="h6" color="primary.main">
                            {profitLossReport.revenue.grossMargin.toFixed(1)}%
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Key Metrics
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Total Transactions:</Typography>
                          <Typography>{profitLossReport.metrics.totalTransactions}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography>Avg. Transaction Value:</Typography>
                          <Typography>₹{profitLossReport.metrics.averageTransactionValue.toFixed(2)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography>Profit Margin:</Typography>
                          <Typography variant="h6" color="success.main">
                            {profitLossReport.metrics.profitMargin.toFixed(1)}%
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};
